/**
 * Thin promise-based wrapper around the Stockfish Web Worker (UCI protocol).
 * Only one search runs at a time; starting a new one cancels the previous.
 */

export interface EngineLine {
  /** First move of the principal variation, in UCI notation (e.g. "e2e4", "e7e8q"). */
  move: string;
  /** Score in centipawns from the side to move's perspective; mates map to ±100000. */
  score: number;
}

export interface SearchOptions {
  fen: string;
  skill: number;
  depth: number;
  movetime: number;
  multiPv?: number;
}

export interface SearchResult {
  bestMove: string;
  lines: EngineLine[];
}

export class EngineUnavailableError extends Error {}
export class SearchCancelledError extends Error {}

class StockfishEngine {
  private worker: Worker | null = null;
  private failed = false;
  private pending: {
    resolve: (r: SearchResult) => void;
    reject: (e: Error) => void;
    lines: Map<number, EngineLine>;
  } | null = null;
  /** bestmove replies still owed for searches we cancelled. */
  private staleReplies = 0;
  private currentMultiPv = 1;

  private ensureWorker(): Worker {
    if (this.failed) throw new EngineUnavailableError('Stockfish failed to load');
    if (this.worker) return this.worker;

    const worker = new Worker('/stockfish.js');
    worker.onmessage = (e: MessageEvent) => {
      if (typeof e.data === 'string') this.onLine(e.data);
    };
    worker.onerror = () => {
      this.failed = true;
      this.pending?.reject(new EngineUnavailableError('Stockfish crashed'));
      this.pending = null;
      worker.terminate();
      this.worker = null;
    };
    worker.postMessage('uci');
    worker.postMessage('isready');
    this.worker = worker;
    return worker;
  }

  /** Load the engine ahead of time so the first move is instant. */
  warmUp() {
    try {
      this.ensureWorker();
    } catch {
      // reported on first search
    }
  }

  newGame() {
    this.cancel();
    if (this.worker) {
      this.worker.postMessage('ucinewgame');
      this.worker.postMessage('isready');
    }
  }

  cancel() {
    if (!this.pending) return;
    this.pending.reject(new SearchCancelledError());
    this.pending = null;
    this.staleReplies += 1;
    this.worker?.postMessage('stop');
  }

  search(options: SearchOptions): Promise<SearchResult> {
    let worker: Worker;
    try {
      worker = this.ensureWorker();
    } catch (err) {
      return Promise.reject(err);
    }
    this.cancel();

    const multiPv = Math.max(1, Math.min(options.multiPv ?? 1, 8));
    return new Promise<SearchResult>((resolve, reject) => {
      this.pending = { resolve, reject, lines: new Map() };
      worker.postMessage(`setoption name Skill Level value ${Math.round(options.skill)}`);
      if (multiPv !== this.currentMultiPv) {
        worker.postMessage(`setoption name MultiPV value ${multiPv}`);
        this.currentMultiPv = multiPv;
      }
      worker.postMessage(`position fen ${options.fen}`);
      worker.postMessage(`go depth ${options.depth} movetime ${options.movetime}`);
    });
  }

  private onLine(line: string) {
    if (line.startsWith('bestmove')) {
      if (this.staleReplies > 0) {
        this.staleReplies -= 1;
        return;
      }
      const pending = this.pending;
      this.pending = null;
      if (!pending) return;
      const bestMove = line.split(' ')[1];
      if (!bestMove || bestMove === '(none)') {
        pending.reject(new EngineUnavailableError('No legal move'));
        return;
      }
      const lines = [...pending.lines.entries()].sort((a, b) => a[0] - b[0]).map(([, l]) => l);
      pending.resolve({ bestMove, lines });
      return;
    }

    if (line.startsWith('info') && this.pending && this.staleReplies === 0) {
      const pv = /\spv\s+(\S+)/.exec(line);
      if (!pv) return;
      const idx = Number(/\smultipv\s+(\d+)/.exec(line)?.[1] ?? 1);
      const cp = /\sscore cp (-?\d+)/.exec(line);
      const mate = /\sscore mate (-?\d+)/.exec(line);
      const score = cp ? Number(cp[1]) : mate ? Math.sign(Number(mate[1])) * 100_000 : 0;
      this.pending.lines.set(idx, { move: pv[1], score });
    }
  }
}

export const engine = new StockfishEngine();

export function parseUci(uci: string): { from: string; to: string; promotion?: 'q' | 'r' | 'b' | 'n' } {
  const promotion = uci[4] as 'q' | 'r' | 'b' | 'n' | undefined;
  return { from: uci.slice(0, 2), to: uci.slice(2, 4), ...(promotion ? { promotion } : {}) };
}
